import { useEffect } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import NotFound from 'src/NotFound';
import Layout from 'src/Layout';
import {
  SitecoreContext,
  ComponentPropsContext,
  StaticPath,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import { componentBuilder } from 'lib/componentBuilder';
import { sitemapFetcher } from 'lib/sitemap-fetcher';
import { PageDataContextProvider } from 'lib/page-data/PageDataContext';
import { SitecoreContextProvider } from 'hooks/useSitecoreContext';
import { notFoundPageService } from 'lib/services/404_PageService';

const SitecorePage = ({
  notFound,
  componentProps,
  layoutData,
  headLinks,
  pageData,
}: SitecorePageProps): JSX.Element => {
  useEffect(() => {
    // Since Sitecore editors do not support Fast Refresh, need to refresh editor chromes after Fast Refresh finished
    handleEditorFastRefresh();
  }, []);

  if (notFound || !layoutData.sitecore.route) {
    // Shouldn't hit this (as long as 'notFound' is being returned below), but just to be safe
    return <NotFound />;
  }

  const isEditing = layoutData.sitecore.context.pageEditing;

  return (
    <PageDataContextProvider value={pageData}>
      <ComponentPropsContext value={componentProps}>
        <SitecoreContext
          componentFactory={componentBuilder.getComponentFactory({ isEditing })}
          layoutData={layoutData}
        >
          <SitecoreContextProvider layoutData={layoutData}>
            <Layout layoutData={layoutData} headLinks={headLinks} />
          </SitecoreContextProvider>
        </SitecoreContext>
      </ComponentPropsContext>
    </PageDataContextProvider>
  );
};

// This function gets called at build and export time to determine
// pages for SSG ("paths", as tokenized array).
export const getStaticPaths: GetStaticPaths = async (context) => {
  // Fallback, along with revalidate in getStaticProps (below),
  // enables Incremental Static Regeneration. This allows us to
  // leave certain (or all) paths empty if desired and static pages
  // will be generated on request (development mode in this example).
  // Alternatively, the entire sitemap could be pre-rendered
  // ahead of time (non-development mode in this example).
  // See https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration

  let paths: StaticPath[] = [];
  let fallback: boolean | 'blocking' = 'blocking';

  if (process.env.NODE_ENV !== 'development' && !process.env.DISABLE_SSG_FETCH) {
    try {
      // Note: Next.js runs export in production mode
      paths = await sitemapFetcher.fetch(context);
    } catch (error) {
      console.log('Error occurred while fetching static paths');
      console.log(error);
    }

    fallback = process.env.EXPORT_MODE ? false : fallback;
  }

  return {
    paths,
    fallback,
  };
};

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation (or fallback) is enabled and a new request comes in.
export const getStaticProps: GetStaticProps = async (context) => {
  let props = await sitecorePagePropsFactory.create(context);
  if (!props.layoutData?.sitecore?.route) {
    const [lang, region] = props?.site?.language.split('-');
    const upperCasedLanguage = region ? `${lang}-${region.toUpperCase()}` : lang;
    const siteName =
      (context.params.path && context.params.path[0]?.replace('_site_', '')) ?? 'Global';
    const resultErrorPages = await notFoundPageService.getNotFoundPageData(
      upperCasedLanguage,
      siteName
    );
    props = {
      ...context,
      layoutData: resultErrorPages.notFoundPage.rendered,
      componentProps: {},
      headLinks: [],
      pageData: {},
    };
  }
  return {
    props,
    revalidate: 5, // In seconds
    notFound: props.notFound,
  };
};

export default SitecorePage;
